import React, { useEffect, useState } from 'react';
import { collection, doc, orderBy, query } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { safeOnSnapshot } from '../../../../../utils/firestoreSubscriptions';
import SubmitButton from '../../../../../components/forms/formComponents/SubmitButton';
import { db } from '../../../../../firebase-client';
import '../../../teams.css';

export default function InboxBroadcastDetailsModal({
  isOpen,
  onClose,
  teamId,
  broadcast,
}) {
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [liveBroadcast, setLiveBroadcast] = useState(broadcast);

  useEffect(() => {
    if (!isOpen || !teamId || !broadcast?.id) return undefined;
    setLoading(true);
    setLiveBroadcast(broadcast);

    const broadcastRef = doc(db, 'teams', teamId, 'inboxBroadcasts', broadcast.id);
    const unsubscribeBroadcast = safeOnSnapshot(broadcastRef, (snap) => {
      if (!snap.exists()) return;
      setLiveBroadcast({
        id: snap.id,
        ...snap.data(),
      });
    });

    const unsubscribeDeliveries = safeOnSnapshot(query(
      collection(db, 'teams', teamId, 'inboxBroadcasts', broadcast.id, 'deliveries'),
      orderBy('recipientName', 'asc')
    ), (snap) => {
      setDeliveries(snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      })));
      setLoading(false);
    }, () => {
      setLoading(false);
    }, { label: 'inbox-broadcast-deliveries', teamId, broadcastId: broadcast.id });

    return () => {
      unsubscribeBroadcast();
      unsubscribeDeliveries();
    };
  }, [broadcast, broadcast?.id, isOpen, teamId]);

  if (!isOpen || !broadcast) return null;

  const createdAt = liveBroadcast?.createdAt?.toDate?.() || liveBroadcast?.createdAt || null;
  const selectedTagFilters = Array.isArray(liveBroadcast?.selectedTagFilters) ? liveBroadcast.selectedTagFilters : [];
  const selectedMemberIds = Array.isArray(liveBroadcast?.selectedMemberIds) ? liveBroadcast.selectedMemberIds : [];
  const deliverySummary = liveBroadcast?.deliverySummary || {};
  const status = String(liveBroadcast?.status || 'queued');
  const getRecipientLabel = (delivery, index) => {
    const name = typeof delivery?.recipientName === 'string' ? delivery.recipientName.trim() : '';
    return name || `Recipient ${index + 1}`;
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={(event) => event.stopPropagation()} style={{ maxWidth: 760 }}>
        <h2 style={{ marginBottom: '0.25rem' }}>Inbox Broadcast</h2>
        <div className="text-muted" style={{ marginBottom: '0.75rem' }}>
          {createdAt ? createdAt.toLocaleString() : '—'} • {liveBroadcast?.createdByName || 'Admin'} • {status}
        </div>

        <div className="card">
          <div className="card-body">
            <h3 style={{ marginBottom: '0.5rem' }}>Message</h3>
            <div style={{ whiteSpace: 'pre-wrap' }}>{liveBroadcast?.message || liveBroadcast?.messagePreview || '—'}</div>
          </div>
        </div>

        <div className="grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
          <div className="card" style={{ padding: '0.75rem' }}>
            <div className="text-muted" style={{ fontSize: '0.85rem' }}>Resolved Recipients</div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{liveBroadcast?.resolvedRecipientCount || 0}</div>
          </div>
          <div className="card" style={{ padding: '0.75rem' }}>
            <div className="text-muted" style={{ fontSize: '0.85rem' }}>Delivered</div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{deliverySummary?.delivered || 0}</div>
          </div>
          <div className="card" style={{ padding: '0.75rem' }}>
            <div className="text-muted" style={{ fontSize: '0.85rem' }}>Failed</div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{deliverySummary?.failed || 0}</div>
          </div>
        </div>

        <div className="card" style={{ marginTop: '0.75rem' }}>
          <div className="card-body">
            <h3 style={{ marginBottom: '0.5rem' }}>Audience</h3>
            {selectedTagFilters.length > 0 && (
              <div style={{ marginBottom: selectedMemberIds.length > 0 ? '0.75rem' : 0 }}>
                <div className="text-muted" style={{ fontSize: '0.9rem', marginBottom: '0.35rem' }}>Selected tags</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {selectedTagFilters.map((tag, index) => (
                    <span
                      key={`${tag?.category || 'uncategorised'}:${tag?.name || index}:${index}`}
                      style={{
                        border: '1px solid var(--team-primary)',
                        borderRadius: '999px',
                        padding: '0.15rem 0.5rem',
                        fontSize: '0.85rem'
                      }}
                    >
                      {tag?.category ? `${tag.category}: ${tag.name}` : tag?.name || 'Tag'}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="text-muted" style={{ fontSize: '0.9rem' }}>
              Directly selected members: <strong>{selectedMemberIds.length}</strong>
            </div>
          </div>
        </div>

        <div className="card" style={{ marginTop: '0.75rem' }}>
          <div className="card-body">
            <h3 style={{ marginBottom: '0.5rem' }}>Deliveries</h3>
            {loading ? (
              <p className="text-muted" style={{ margin: 0 }}>Loading delivery details…</p>
            ) : deliveries.length === 0 ? (
              <p className="text-muted" style={{ margin: 0 }}>No delivery details available.</p>
            ) : (
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {deliveries.map((delivery, index) => (
                  <div
                    key={delivery.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto',
                      gap: '0.75rem',
                      alignItems: 'center',
                      border: '1px solid #e5e7eb',
                      borderRadius: 10,
                      padding: '0.6rem 0.75rem'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>{getRecipientLabel(delivery, index)}</div>
                      <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                        {delivery?.status || 'unknown'}
                        {delivery?.error ? ` • ${delivery.error}` : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn--secondary btn--xs"
                      disabled={!delivery?.threadId}
                      onClick={() => {
                        if (!delivery?.threadId) return;
                        onClose?.();
                        navigate(`/messages/${delivery.threadId}`);
                      }}
                    >
                      Open thread
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-actions" style={{ marginTop: '1rem' }}>
          <SubmitButton type="button" onClick={onClose} label="Close" />
        </div>
      </div>
    </div>
  );
}
